import { Product, ProductVariant, calculateDiscountedPrice } from './productService';

export interface CartItem {
  id: string; // Unique cart item ID (productId + variantId)
  productId: string;
  productName: string;
  price: number;
  discount?: number; // Discount percentage (0-100), mirrors productService
  quantity: number;
  image: string;
  variant?: ProductVariant | null;
  variantId?: string | null;
  variantText?: string | null;
  maxStock: number;
  addedAt: string;
}

const CART_STORAGE_KEY = 'shopping_cart';

// Get cart from localStorage
export const getCart = (): CartItem[] => {
  try {
    const cartData = localStorage.getItem(CART_STORAGE_KEY);
    if (!cartData) return [];
    return JSON.parse(cartData);
  } catch (error) {
    console.error('Error reading cart from localStorage:', error);
    return [];
  }
};

// Save cart to localStorage
const saveCart = (cart: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    // Dispatch custom event for cart updates
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

// Generate unique cart item ID
const generateCartItemId = (productId: string, variantId?: string | null): string => {
  return variantId ? `${productId}-${variantId}` : productId;
};

// Add item to cart
export const addToCart = (
  product: Product,
  quantity: number,
  selectedVariant?: ProductVariant | null
): { success: boolean; message: string } => {
  try {
    const cart = getCart();
    const variantId = selectedVariant?.id || null;
    const cartItemId = generateCartItemId(product.id, variantId);

    // Check if item already exists in cart
    const existingItemIndex = cart.findIndex(item => item.id === cartItemId);

    if (existingItemIndex !== -1) {
      // Update quantity of existing item
      const existingItem = cart[existingItemIndex];
      const newQuantity = existingItem.quantity + quantity;

      // Check if new quantity exceeds stock
      if (newQuantity > existingItem.maxStock) {
        return {
          success: false,
          message: `Cannot add more. Only ${existingItem.maxStock} available in stock.`
        };
      }

      cart[existingItemIndex].quantity = newQuantity;
    } else {
      // Add new item to cart
      const maxStock = selectedVariant?.stockQuantity || product.stockQuantity;
      const price = selectedVariant?.price ?? product.price;
      const discount = selectedVariant?.discount ?? product.discount;

      const newItem: CartItem = {
        id: cartItemId,
        productId: product.id,
        productName: product.name,
        price,
        discount,
        quantity,
        image: product.images?.[0] || '',
        variant: selectedVariant || null,
        variantId: variantId,
        variantText: selectedVariant ? getVariantDisplayText(selectedVariant) : null,
        maxStock,
        addedAt: new Date().toISOString()
      };

      cart.push(newItem);
    }

    saveCart(cart);
    return {
      success: true,
      message: 'Item added to cart successfully!'
    };
  } catch (error) {
    console.error('Error adding to cart:', error);
    return {
      success: false,
      message: 'Failed to add item to cart.'
    };
  }
};

// Remove item from cart
export const removeFromCart = (cartItemId: string): { success: boolean; message: string } => {
  try {
    const cart = getCart();
    const filteredCart = cart.filter(item => item.id !== cartItemId);
    
    if (filteredCart.length === cart.length) {
      return {
        success: false,
        message: 'Item not found in cart.'
      };
    }

    saveCart(filteredCart);
    return {
      success: true,
      message: 'Item removed from cart.'
    };
  } catch (error) {
    console.error('Error removing from cart:', error);
    return {
      success: false,
      message: 'Failed to remove item from cart.'
    };
  }
};

// Update item quantity in cart
export const updateCartItemQuantity = (
  cartItemId: string,
  quantity: number
): { success: boolean; message: string } => {
  try {
    if (quantity < 1) {
      return removeFromCart(cartItemId);
    }

    const cart = getCart();
    const itemIndex = cart.findIndex(item => item.id === cartItemId);

    if (itemIndex === -1) {
      return {
        success: false,
        message: 'Item not found in cart.'
      };
    }

    const item = cart[itemIndex];

    // Check if quantity exceeds stock
    if (quantity > item.maxStock) {
      return {
        success: false,
        message: `Cannot add more. Only ${item.maxStock} available in stock.`
      };
    }

    cart[itemIndex].quantity = quantity;
    saveCart(cart);

    return {
      success: true,
      message: 'Cart updated successfully.'
    };
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    return {
      success: false,
      message: 'Failed to update cart.'
    };
  }
};

// Clear entire cart
export const clearCart = (): { success: boolean; message: string } => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: [] }));
    return {
      success: true,
      message: 'Cart cleared successfully.'
    };
  } catch (error) {
    console.error('Error clearing cart:', error);
    return {
      success: false,
      message: 'Failed to clear cart.'
    };
  }
};

// Get cart item count
export const getCartItemCount = (): number => {
  const cart = getCart();
  return cart.reduce((total, item) => total + item.quantity, 0);
};

// Get cart total price — uses discounted price per item if applicable
export const getCartTotal = (): number => {
  const cart = getCart();
  return cart.reduce((total, item) => {
    const effectivePrice = calculateDiscountedPrice(item.price, item.discount);
    return total + (effectivePrice * item.quantity);
  }, 0);
};

// Check if product/variant is in cart
export const isInCart = (productId: string, variantId?: string | null): boolean => {
  const cart = getCart();
  const cartItemId = generateCartItemId(productId, variantId);
  return cart.some(item => item.id === cartItemId);
};

// Get quantity of specific item in cart
export const getCartItemQuantity = (productId: string, variantId?: string | null): number => {
  const cart = getCart();
  const cartItemId = generateCartItemId(productId, variantId);
  const item = cart.find(item => item.id === cartItemId);
  return item?.quantity || 0;
};

// Helper function to get variant display text
const getVariantDisplayText = (variant: ProductVariant): string => {
  if (!variant.variantSelections || variant.variantSelections.length === 0) {
    return '';
  }
  
  return variant.variantSelections
    .map(selection => selection.variantValue)
    .join(' / ');
};

// Subscribe to cart updates
export const subscribeToCartUpdates = (callback: (cart: CartItem[]) => void): (() => void) => {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent;
    callback(customEvent.detail);
  };

  window.addEventListener('cartUpdated', handler);

  // Return unsubscribe function
  return () => {
    window.removeEventListener('cartUpdated', handler);
  };
};